import React, {useEffect, useMemo, useRef, useState} from "react";
import Card from "components/card";

import {
    createColumnHelper, flexRender, getCoreRowModel, getPaginationRowModel, getSortedRowModel, useReactTable,
} from "@tanstack/react-table";
import {Link, useNavigate} from "react-router-dom";
import {FaCheck} from "react-icons/fa";
import {RiCustomerService2Fill} from "react-icons/ri";
import {Fab} from "react-tiny-fab";
import {IoCloseSharp} from "react-icons/io5";
import {useSelector} from "react-redux";
import {applyFilters, clearFilters} from "../../../../variables/utils";
import {parse} from 'date-fns';
import {GiTechnoHeart} from "react-icons/gi";

const columnHelper = createColumnHelper();

export default function ChatLogs({tableData, viewMore = false, nbrows = 8}) {
    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState('');
    const [allData, setAllData] = useState(tableData);
    const inputRef = useRef();
    let allfilters = useSelector(state => state.filters)
    let perday = useSelector(state => state.perday)


    const [pagination, setPagination] = React.useState({
        pageIndex: 0, pageSize: nbrows,
    })

    const filteredData = useMemo(() => {
        return allData.filter(row => Object.values(row).some(value => String(value).toLowerCase().includes(searchQuery.toLowerCase())));
    }, [allData, searchQuery]);


    const columns = [columnHelper.accessor("FileName", {
        id: "FileName",
        header: () => (<p className="text-sm font-bold text-gray-600 dark:text-white">Name</p>),
        cell: (info) => (<p className="text-sm font-bold text-navy-700 dark:text-white">
            <Link to={'/all-chats/' + info.getValue()}>{info.getValue()}</Link>
        </p>),
    }), columnHelper.accessor("Date", {
        id: "Date", header: () => (<p className="text-sm font-bold text-gray-600 dark:text-white">
            Date
        </p>), cell: (info) => (<div className="flex items-center">
            {info.getValue()}
        </div>),
        sortingFn: (rowA, rowB, columnId) => {
            const dateA = parse(rowA.getValue(columnId), 'MM/dd/yyyy', new Date());
            const dateB = parse(rowB.getValue(columnId), 'MM/dd/yyyy', new Date());
            // Compare the dates
            return dateA - dateB;
        },
    }), columnHelper.accessor("CriticalIssues", {
        id: "CriticalIssues",
        header: () => (<p className="text-sm font-bold text-gray-600 dark:text-white">Critical Issue</p>),
        cell: (info) => (<p className="text-sm font-bold text-navy-700 dark:text-white">
            {info.getValue()}
        </p>),
    }), columnHelper.accessor("BotSolved", {
        id: "BotSolved",
        header: () => (<p className="text-sm font-bold text-gray-600 dark:text-white">Bot Solved</p>),
        cell: (info) => (<p className="text-sm font-bold text-navy-700 dark:text-white">
            {info.getValue().toLowerCase() == "null" ? "Unknown" : info.getValue().toLowerCase()}
        </p>),
    }), columnHelper.accessor("moderatorfeedback", {
        id: "moderatorfeedback",
        header: () => (<p className="text-sm font-bold text-gray-600 dark:text-white">Customer Support</p>),
        cell: (info) => (<p className="text-sm flex flex-row gap-4 font-bold text-navy-700 dark:text-white">
            {info.row.original.moderatorfeedback ?
                <FaCheck title={"By Customer support"} className={"w-6 h-6 cursor-pointer"}/> : ""}
        </p>),
    }), columnHelper.accessor("technicalFeedback", {
        id: "technicalFeedback",
        header: () => (<p className="text-sm font-bold text-gray-600 dark:text-white">Technical</p>),
        cell: (info) => (<p className="text-sm flex flex-row gap-4 font-bold text-navy-700 dark:text-white">
            {info.row.original.technicalFeedback ?
                <FaCheck title={"By Technical person"} className={"w-6 h-6 cursor-pointer"}/> : ""}
        </p>),
    })]; // eslint-disable-next-line


    const table = useReactTable({
        data: filteredData,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        initialState: {
            sorting: [{id: "Date", desc: true}],
        },
        onPaginationChange: setPagination,
        state: {
            pagination,
        },
        debugTable: false,
    });


    useEffect(() => {

        window.addEventListener("keydown", function (e) {
            if (e.keyCode === 114 || (e.ctrlKey && e.keyCode === 70)) {
                if (inputRef.current) {
                    e.preventDefault();
                    inputRef.current.focus();
                }
            }
        })
    }, [inputRef])


    useEffect(() => {
        if (tableData.length > 0) {
            let all = applyFilters(tableData, allfilters);
            setAllData(all);
        }
    }, [tableData, allfilters])


    return (<Card extra={"w-full h-full px-6 pb-6 sm:overflow-x-auto"}>
        {allfilters.length > 0 && <Fab
            mainButtonStyles={{backgroundColor: '#007bff'}}
            position={{bottom: 24, right: 24}}
            icon={<IoCloseSharp title={"Reset Filters"}/>}
            event='click'
            onClick={() => clearFilters(perday)}
        >

        </Fab>}

        {viewMore && <div className="relative flex items-center justify-between pt-4">
            <div className="text-xl font-bold text-navy-700 dark:text-white">
                Chats History
            </div>

            <button
                onClick={() => {
                    navigate("/all-chats");
                }}
                className="!linear z-[1] flex items-center justify-center rounded-lg bg-lightPrimary p-2 text-brand-500 !transition !duration-200 hover:bg-gray-100 active:bg-gray-200 dark:bg-navy-700 dark:text-white dark:hover:bg-white/20 dark:active:bg-white/10">
                <div className="text-sm text-navy-700 dark:text-white">
                    View All
                </div>
            </button>
        </div>}


        <div className="mt-8 overflow-x-scroll xl:overflow-x-hidden">
            <div className="pagination-controls flex justify-between items-center w-full space-x-4">
                <button
                    onClick={() => table.previousPage()}
                    disabled={!table.getCanPreviousPage()}
                    className="px-4 py-2"
                >
                    Previous
                </button>

                <span className="flex-1 text-center">
        Page{" "}
                    <strong>
            {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
        </strong>
    </span>

                <button
                    onClick={() => table.nextPage()}
                    disabled={!table.getCanNextPage()}
                    className="px-4 py-2"
                >
                    Next
                </button>

                <span className="flex items-center">
        | Go to page:{" "}
                    <input
                        type="number"
                        value={table.getState().pagination.pageIndex + 1}
                        onChange={(e) => {
                            const page = e.target.value ? Number(e.target.value) - 1 : 0;
                            table.setPageIndex(page);
                        }}
                        className="ml-2 border rounded w-16 text-center"
                    />
    </span>

                <select
                    value={table.getState().pagination.pageSize}
                    onChange={(e) => {
                        table.setPageSize(Number(e.target.value));
                    }}
                    className="border rounded px-2 py-1"
                >
                    {[25, 50, 100].map((pageSize) => (<option key={pageSize} value={pageSize}>
                        Show {pageSize}
                    </option>))}
                </select>
            </div>


            <div className={"p-2"}>
                <input
                    className={"w-full p-3 bg-gray-50 rounded-lg"}
                    ref={el => inputRef.current = el}
                    type="text"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                />
            </div>

            <table className="w-full">
                <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id} className="!border-px !border-gray-400">
                        {headerGroup.headers.map((header) => {
                            return (<th
                                key={header.id}
                                colSpan={header.colSpan}
                                onClick={header.column.getToggleSortingHandler()}
                                className="cursor-pointer border-b-[1px] border-gray-200 pt-4 pb-2 pr-4 text-start"
                            >
                                <div className="items-center justify-between text-xs text-gray-200">
                                    {flexRender(header.column.columnDef.header, header.getContext())}
                                    {{
                                        asc: "", desc: "",
                                    }[header.column.getIsSorted()] ?? null}
                                </div>
                            </th>);
                        })}
                    </tr>))}
                </thead>
                <tbody>
                {table
                    .getRowModel()
                    .rows
                    .map((row) => {
                        return (<tr key={row.id}>
                            {row.getVisibleCells().map((cell) => {
                                return (<td
                                    key={cell.id}
                                    className="min-w-[150px] border-white/0 py-3  pr-4"
                                >
                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                </td>);
                            })}
                        </tr>);
                    })}
                </tbody>
            </table>
        </div>


        <div className="pagination-controls flex justify-between items-center w-full space-x-4">
            <button
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
                className="px-4 py-2"
            >
                Previous
            </button>

            <span className="flex-1 text-center">
        Page{" "}
                <strong>
            {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
        </strong>
    </span>

            <button
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
                className="px-4 py-2"
            >
                Next
            </button>

            <span className="flex items-center">
        | Go to page:{" "}
                <input
                    type="number"
                    value={table.getState().pagination.pageIndex + 1}
                    onChange={(e) => {
                        const page = e.target.value ? Number(e.target.value) - 1 : 0;
                        table.setPageIndex(page);
                    }}
                    className="ml-2 border rounded w-16 text-center"
                />
    </span>

            <select
                value={table.getState().pagination.pageSize}
                onChange={(e) => {
                    table.setPageSize(Number(e.target.value));
                }}
                className="border rounded px-2 py-1"
            >
                {[25, 50, 100].map((pageSize) => (<option key={pageSize} value={pageSize}>
                    Show {pageSize}
                </option>))}
            </select>
        </div>

    </Card>);
}
